/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  СТОР АВТОРИЗАЦИИ — кто вошёл, чем он предъявляется и как это переживает F5
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *   state:   user · accessToken · loggingIn · ready
 *   getters: isAuthenticated · role · isAdmin · displayName · initials
 *   actions: login · logout · refresh · bootstrap · reset · setAccessToken · applyTokens
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  ⚠️ ЗДЕСЬ ЖИВЁТ ТОЛЬКО СИСТЕМНАЯ РОЛЬ
 * ───────────────────────────────────────────────────────────────────────────
 *
 * `user.role` — это `SystemRole` (`admin` / `user`), и она едет в claim
 * access-токена. Роли в конкретных мероприятиях (`owner` / `viewer`) сюда **не
 * попадают и попасть не могут**: они принадлежат паре (мероприятие,
 * пользователь), меняются, пока токен жив, и узнать их можно только загрузив
 * состав мероприятия. За них отвечает `stores/event.ts`.
 *
 * Практическое следствие: `isAdmin` годится для экранов «Пользователи» и
 * «Создать мероприятие», и **не годится** ни для чего внутри мероприятия. Там
 * спрашивают `useEventStore().canManage`.
 *
 * ⚠️ Плоского списка `permissions` в этом контракте нет — `GET /auth/me` отдаёт
 * только `user`. Не заводите его на клиенте: вычисленный из роли список прав
 * — это копия серверной таблицы, которая разъедется с ней на первом же
 * изменении, и хуже всего то, что разъедется молча.
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  ДВЕ ВЕЩИ, РАДИ КОТОРЫХ ЭТОТ ФАЙЛ ВЫГЛЯДИТ ИМЕННО ТАК
 * ───────────────────────────────────────────────────────────────────────────
 *
 * **1. `bootstrap()` мемоизирован в один промис.** Гвард ждёт его на *каждой*
 * навигации. Access-токен живёт только в памяти, поэтому на холодной загрузке
 * восстановление сессии — это сетевой round-trip, и две быстрые навигации на
 * старте (редирект `/` → `/events`, клик по ссылке в момент загрузки) без
 * мемоизации дали бы **два** `POST /auth/refresh`. Сервер ротирует refresh на
 * каждое использование, второй вызов предъявил бы уже погашенный токен — и
 * живая 30-дневная сессия умерла бы на ровном месте.
 *
 * **2. `refresh()` делегирует в `refreshOnce()` из `lib/http.ts`, а не зовёт
 * `authApi.refresh()`.** Single-flight там общий на всё приложение: ротация,
 * запущенная гвардом, и ротация, запущенная 401-й из зависшего компонента,
 * обязаны схлопнуться в **один** запрос. Своя копия вызова рядом с очередью
 * ровно эту гарантию и ломает.
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  ⚠️ МОСТ «ТРАНСПОРТ ↔ СТОР» СОБИРАЕТСЯ ЗДЕСЬ
 * ───────────────────────────────────────────────────────────────────────────
 *
 * `lib/http.ts` не имеет права импортировать стор (цикл: стор → api → http →
 * стор), поэтому связь односторонняя и инъектируется отсюда. Без моста
 * приложение ломается ровно в том сценарии, ради которого он и нужен: refresh
 * отдал 401 → транспорт вычистил хранилище → но стор об этом не узнал,
 * `isAuthenticated` остался `true`, гвард пускает на приватный маршрут, и
 * человек сидит на экране, где каждый запрос молча падает.
 */

import { defineStore, getActivePinia } from 'pinia'
import { computed, ref } from 'vue'

import { authApi } from '@/api/auth'
import type { ApiError } from '@/lib/api-error'
import { normalizeError } from '@/lib/api-error'
import { refreshOnce, setAuthLostHandler, setTokensRotatedHandler } from '@/lib/http'
import { tokenStorage } from '@/lib/token-storage'
import type { SystemRole, TokenPair, User } from '@/types/api'

/**
 * Транспортная неудача, а не авторитетный отказ: сервер не ответил (0) или
 * ответил 5xx. Отличается от 4xx тем, что refresh-токен при ней **остаётся**
 * на диске (`lib/http.ts`, `isSessionLost`), то есть сессию ещё можно спасти —
 * и значит `bootstrap()` имеет право попробовать ещё раз на следующей навигации.
 */
function isTransient(error: ApiError): boolean {
  return error.status === 0 || error.status >= 500
}

export const useAuthStore = defineStore('auth', () => {
  /**
   * Инстанс пинии захватывается на создании стора: `reset()` обходит по нему
   * все остальные сторы. Брать `getActivePinia()` в момент вызова было бы
   * хрупко — `reset()` зовётся и из хендлеров транспорта, то есть вне
   * гарантированного контекста.
   */
  const pinia = getActivePinia()

  /* ═══════════════════════════ state ═══════════════════════════ */

  /** Профиль из `GET /auth/me` (или из ответа логина). `null` — не вошли. */
  const user = ref<User | null>(null)

  /**
   * **Зеркало** access-токена для реактивности: настоящее хранилище —
   * модульная переменная в `lib/token-storage.ts`, и на диск он не ложится
   * никогда. Отсюда же и необходимость `bootstrap()`: на холодной загрузке
   * здесь всегда `null`, даже при совершенно живой сессии.
   */
  const accessToken = ref<string | null>(null)

  /** Форма логина в полёте — для `disabled` и спиннера на кнопке. */
  const loggingIn = ref(false)

  /** `bootstrap()` отработал (успешно или нет). */
  const ready = ref(false)

  /* ═══════════════════════════ getters ═════════════════════════ */

  const isAuthenticated = computed(() => accessToken.value !== null && user.value !== null)

  /** **Системная** роль. Роли в мероприятиях — в `stores/event.ts`. */
  const role = computed<SystemRole | null>(() => user.value?.role ?? null)

  /**
   * Супер-администратор системы: проходит любую проверку прав, пока учётная
   * запись не отключена.
   *
   * ⚠️ Это аффорданс UI, а не контроль доступа. Авторитет — бэкенд: он вернёт
   * 403 (или 404 на чужое мероприятие) независимо от того, что решил фронт.
   */
  const isAdmin = computed(() => role.value === 'SYSTEM_ROLE_ADMIN')

  const displayName = computed(() => {
    const u = user.value
    if (!u) return ''
    return u.fullName || u.username || u.email
  })

  const initials = computed(() => {
    const source = displayName.value.trim()
    if (!source) return ''
    const words = source.split(/[\s._-]+/).filter(Boolean)
    const letters = words.length >= 2
      ? words[0]![0]! + words[1]![0]!
      : source.slice(0, 2)
    return letters.toUpperCase()
  })

  /* ═══════════════════════════ внутреннее ══════════════════════ */

  /**
   * Мемоизация `bootstrap()`. **Не сбрасывается после успеха**: восстанавливать
   * сессию во второй раз за жизнь вкладки незачем — дальше её поддерживает
   * `refreshOnce()` из интерцептора. Сбрасывается только при транспортной
   * неудаче, чтобы «бэкенд полежал две секунды» не означало «до перезагрузки
   * страницы вы аноним».
   */
  let bootstrapPromise: Promise<void> | null = null

  /**
   * Гасит соседние сторы, чтобы пользователь B на секунду не увидел данные
   * пользователя A — и, что важнее, чтобы он не увидел **мероприятие**, к
   * которому у него нет доступа: `stores/event.ts` держит и роль в нём.
   *
   * Обход реестра пинии, а не поимённый список: строку на каждый новый стор
   * рано или поздно забудут дописать. Обходим только **инстанцированные**
   * сторы — тот, который в этой вкладке ни разу не открывали, ничего и не
   * хранит.
   *
   * `try/catch` обязателен: у setup-стора, не объявившего собственный `$reset`,
   * пинийная заглушка в dev-сборке **бросает**, а в прод-сборке молчит. Для
   * нас это не ошибка, а «нечего сбрасывать».
   */
  function resetPeerStores(): void {
    const registry = (pinia as { _s?: Map<string, { $id: string, $reset?: unknown }> } | undefined)?._s
    if (!registry) return

    for (const store of registry.values()) {
      if (store.$id === 'auth') continue
      const storeReset = store.$reset
      if (typeof storeReset !== 'function') continue
      try {
        (storeReset as () => void).call(store)
      } catch {
        /* сторонний стор без собственного `$reset` — сбрасывать нечего */
      }
    }
  }

  /* ═══════════════════════════ actions ═════════════════════════ */

  /**
   * Кладёт пару токенов и в хранилище, и в зеркало.
   *
   * `rememberMe` **не передаётся**: хранилище остаётся тем, которое выбрал
   * логин. Это важно для смены пароля — она ротирует текущую сессию, но
   * `rememberMe` на ней сервер не меняет, и клиент не имеет права молча
   * переехать из `sessionStorage` в `localStorage`.
   */
  function applyTokens(tokens: TokenPair): void {
    tokenStorage.save(tokens)
    accessToken.value = tokens.accessToken
  }

  /**
   * Только зеркало, без записи на диск.
   *
   * Существует ради моста с транспортом: к моменту `onTokensRotated`
   * `lib/http.ts` уже вызвал `tokenStorage.save()`, и вторая запись была бы
   * лишней. Для «сохранить новую пару» есть `applyTokens()`.
   */
  function setAccessToken(token: string | null): void {
    accessToken.value = token
  }

  /**
   * Зачистка **только оперативной памяти**: стор, зеркало access-токена и
   * соседние сторы. Refresh-токен на диске при этом не трогается.
   *
   * Нужна отдельно от `reset()` ровно для одного случая — транспортного сбоя в
   * `bootstrap()`. Сервер, который не ответил или ответил 502, ничего не сказал
   * о действительности refresh-токена: он не был отвергнут, он не был даже
   * рассмотрен. Стереть его в этот момент значит превратить двухсекундный
   * перезапуск бэкенда в разлогин тридцатидневной сессии, причём необратимый:
   * «повторить попытку» уже нечем.
   */
  function resetState(): void {
    user.value = null
    accessToken.value = null
    loggingIn.value = false
    resetPeerStores()
  }

  /**
   * Полная зачистка: выход, потеря сессии, `clear()` из соседней вкладки.
   *
   * Идемпотентна и **никогда не бросает** — её зовут из хендлеров транспорта,
   * где исключение подменило бы собой настоящую причину провала.
   */
  function reset(): void {
    resetState()
    tokenStorage.clear()
  }

  /**
   * Ротация токена силами общего single-flight.
   *
   * ⚠️ Именно `refreshOnce()`, а не `authApi.refresh()` — см. шапку файла.
   * Зеркало обновляем и здесь: хендлер `onTokensRotated` сделает то же самое,
   * но `refresh()` обязан работать и без собранного моста (тесты, заглушки).
   */
  async function refresh(): Promise<void> {
    const token = await refreshOnce()
    accessToken.value = token
  }

  /**
   * Вход. `rememberMe` решает сразу две вещи: срок refresh-токена на сервере
   * (12 ч / 30 дней) и хранилище на клиенте (`sessionStorage` / `localStorage`).
   *
   * Ошибку **пробрасываем наверх** — её разбирает `LoginView`:
   * `INVALID_CREDENTIALS` рисуется под полем пароля, `RATE_LIMITED` и
   * `USER_DISABLED` — над формой. Стор не знает, где у формы поля.
   */
  async function login(loginValue: string, password: string, rememberMe: boolean): Promise<void> {
    loggingIn.value = true
    try {
      const response = await authApi.login({ login: loginValue, password, rememberMe })

      tokenStorage.save(response.tokens, rememberMe)
      accessToken.value = response.tokens.accessToken
      user.value = response.user

      // Сессия восстановлена явным входом — ближайшему проходу гварда
      // бутстрапить нечего.
      bootstrapPromise = Promise.resolve()
      ready.value = true
    } finally {
      loggingIn.value = false
    }
  }

  /**
   * Вход по одноразовому коду устройства.
   *
   * Зеркалит `login()`, кроме одного: **«запомнить меня» здесь нет и быть не
   * может**. Срок сессии решает сервер (смена, а не месяц), и хранилище
   * поэтому `sessionStorage` — телефон на входе общий, и refresh-токен не
   * должен переживать закрытие вкладки.
   *
   * Ошибку пробрасываем наверх: её показывает экран входа по коду, и текст там
   * один на все причины — см. `DEVICE_TICKET_INVALID` в `lib/api-error.ts`.
   */
  async function loginWithTicket(ticket: string): Promise<void> {
    loggingIn.value = true
    try {
      const response = await authApi.redeemDeviceTicket({ ticket })

      tokenStorage.save(response.tokens, false)
      accessToken.value = response.tokens.accessToken
      user.value = response.user

      bootstrapPromise = Promise.resolve()
      ready.value = true
    } finally {
      loggingIn.value = false
    }
  }

  /**
   * Выход. Провал серверного вызова **проглатывается**: человек нажал «Выйти»,
   * и запереть его внутри панели из-за отвалившейся сети нельзя. Локально мы
   * вышли в любом случае — отсюда `reset()` вне зависимости от исхода.
   *
   * `skipAuth` на самом запросе стоит в `api/auth.ts`: выход обязан срабатывать
   * и с протухшим access-токеном.
   */
  async function logout(): Promise<void> {
    const refreshToken = tokenStorage.getRefreshToken()
    try {
      if (refreshToken) await authApi.logout({ refreshToken })
    } catch {
      /* сервер не подтвердил выход — локально мы всё равно вышли */
    } finally {
      reset()
    }
  }

  /**
   * Восстановление сессии на холодной загрузке. **Мемоизирован** — см. шапку.
   *
   * Контракт:
   *   • нет refresh-токена → мы аноним, ни одного запроса не делаем;
   *   • иначе `refresh()` (общий single-flight) → `authApi.me()`;
   *   • любая неудача → `reset()` и продолжаем анонимом;
   *   • всегда заканчивается `ready = true`;
   *   • **никогда не бросает** — гвард оборачивает вызов в `try/catch`, но
   *     нуждаться в этом он не должен.
   */
  async function bootstrap(): Promise<void> {
    bootstrapPromise ??= runBootstrap()
    return bootstrapPromise
  }

  async function runBootstrap(): Promise<void> {
    try {
      if (!tokenStorage.getRefreshToken()) {
        // Штатный путь холодного старта неаутентифицированного пользователя,
        // а не сбой: предъявлять просто нечего.
        reset()
        return
      }

      await refresh()

      /**
       * `me()` — не формальность даже при том, что `RefreshResponse` уже несёт
       * пользователя. Ответ refresh'а обрабатывает мост (`onTokensRotated`),
       * который может быть не собран в тестах; здесь же профиль кладётся
       * явно и наверняка.
       */
      const me = await authApi.me()
      user.value = me.user
    } catch (err) {
      const error = normalizeError(err)

      if (isTransient(error)) {
        /*
         * Сеть/5xx: refresh-токен цел и остаётся на диске — транспорт стирает
         * его только на авторитетном 4xx. Поэтому здесь **`resetState()`, а не
         * `reset()`**: сбрасываем стор, но не хранилище.
         *
         * Это не микрооптимизация. `reset()` тут стирал бы ровно тот токен,
         * ради которого следующая попытка и затевается: пользователь с живой
         * 30-дневной сессией, открывший вкладку в секунду перезапуска бэкенда,
         * оказывался бы разлогинен окончательно — «попробовать ещё раз» было бы
         * уже нечем.
         *
         * Мемо снимаем, чтобы следующая навигация попробовала снова.
         */
        resetState()
        bootstrapPromise = null
      } else {
        // Авторитетный отказ (4xx): токен уже мёртв, хранилище чистит `reset()`
        // — даже если ротация до этого не дошла (например, упал `GET /auth/me`).
        reset()
      }
    } finally {
      ready.value = true
    }
  }

  /* ═══════════════════════ мост с транспортом ══════════════════ */

  function wireBridge(): void {
    // Сессия потеряна безвозвратно: refresh отдал 4xx, токены уже стёрты.
    setAuthLostHandler(() => {
      reset()
      void redirectToLogin()
    })

    /**
     * Токены отротировались. `user` приезжает вместе с ними и **перечитан из
     * БД**: если системную роль сменили, здесь она уже новая, тогда как claim
     * в старом access-токене врал бы до конца своего TTL. (Смена роли к тому же
     * гасит все сессии цели, так что до этой ветки дело обычно не доходит — но
     * рассчитывать на это нельзя.)
     */
    setTokensRotatedHandler((tokens, rotatedUser) => {
      setAccessToken(tokens.accessToken)
      if (rotatedUser) user.value = rotatedUser
    })

    // Соседняя вкладка вычистила storage (выход или отзыв) — эта тоже выходит.
    tokenStorage.onExternalClear(() => {
      reset()
      void redirectToLogin({ preserveTarget: false })
    })
  }

  /**
   * Уход на форму входа после потери сессии.
   *
   * Роутер импортируется **динамически**: `router/index.ts` → `guards.ts` →
   * этот файл, то есть статический импорт замкнул бы цикл. К моменту вызова
   * модуль давно загружен, так что это просто разрешённый промис из кеша.
   *
   * `preserveTarget: false` для кросс-вкладочного выхода: там сессию погасили
   * намеренно, и возвращать человека ровно туда, откуда его выкинули, — не то,
   * о чём он просил.
   */
  async function redirectToLogin(options: { preserveTarget?: boolean } = {}): Promise<void> {
    const { preserveTarget = true } = options
    try {
      const { default: router } = await import('@/router')
      const current = router.currentRoute.value
      if (current.name === 'login') return

      const query = preserveTarget && current.fullPath !== '/'
        ? { redirect: current.fullPath }
        : undefined

      await router.replace({ name: 'login', query })
    } catch {
      /* навигация отменена другим гвардом — не наша забота */
    }
  }

  wireBridge()

  return {
    // state
    user,
    accessToken,
    loggingIn,
    ready,
    // getters
    isAuthenticated,
    role,
    isAdmin,
    displayName,
    initials,
    // actions
    login,
    loginWithTicket,
    logout,
    refresh,
    bootstrap,
    reset,
    setAccessToken,
    applyTokens,
  }
})
